import StatusWarningIcon from "@atlaskit/icon/core/status-warning";
import CrossIcon from "@atlaskit/icon/core/cross";
import {token} from "@atlaskit/tokens";
import {useNavigate} from "@tanstack/react-router";
import type {Dispatch, ReactNode, SetStateAction} from "react";
import {useEffect, useState} from "react";
import {createPortal} from "react-dom";
import {BottomDrawer} from "~/components/BottomDrawer";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {archiveEventContainerLikeArchiveDropdown} from "~/utilities/archiveEventContainerWithEvents";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {showSuccessToast} from "~/utilities/utilities";
import {useShowErrorFlag} from "~/hooks/useShowErrorFlag";
import {useRequiredEventContainerTemplate} from "~/contexts/workspaceEventContainerTemplatesContext";
import {configEventContainerListHref} from "~/workspaceConfig/configHref";

function ConfirmationTitleWithIcon({
    title,
    appearance,
}: {
    title: string;
    appearance: "danger" | "warning";
}) {
    const iconColor = appearance === "warning" ? token("color.icon.warning") : token("color.icon.danger");

    return (
        <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-ds-space-100 min-w-0">
            <span className="mt-0.5 shrink-0">
                <StatusWarningIcon label="" color={iconColor} />
            </span>
            <span
                className="min-w-0 text-[1.25rem] leading-ds-space-300 font-ds-font-weight-bold"
                style={{color: token("color.text"), fontFamily: "Inter"}}
            >
                {title}
            </span>
        </div>
    );
}

function useMobileSheetMidHeight(): number {
    const [height, setHeight] = useState(400);

    useEffect(() => {
        const updateHeight = () => {
            setHeight(Math.round(window.innerHeight * 0.5));
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => {
            window.removeEventListener("resize", updateHeight);
        };
    }, []);

    return height;
}

function DesktopCenteredConfirmationDialog({
    isOpen,
    onClose,
    title,
    titleAppearance,
    children,
    footer,
}: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    titleAppearance: "danger" | "warning";
    children: ReactNode;
    footer: ReactNode;
}) {
    useEffect(() => {
        if (isOpen !== true) {
            return;
        }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, onClose]);

    if (isOpen !== true) {
        return null;
    }

    return createPortal(
        <>
            <button
                type="button"
                aria-label="Close dialog"
                onClick={onClose}
                className="fixed inset-0 z-[510] cursor-default border-0 p-0"
                style={{
                    backgroundColor: token("color.blanket"),
                }}
            />
            <div
                className="fixed inset-0 z-[511] grid place-items-center p-ds-space-300 pointer-events-none"
                role="presentation"
            >
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="event-container-archive-confirmation-title"
                    className="pointer-events-auto grid w-full max-w-[400px] content-start overflow-hidden rounded-ds-border-radius"
                    style={{
                        background: token("elevation.surface.overlay"),
                        boxShadow: token(
                            "elevation.shadow.overlay",
                            "0 0 1px 0 rgba(9, 30, 66, 0.31), 0 8px 12px 0 rgba(9, 30, 66, 0.15)",
                        ),
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-ds-space-100 px-ds-space-300 pt-ds-space-300 pb-ds-space-200">
                        <div id="event-container-archive-confirmation-title">
                            <ConfirmationTitleWithIcon title={title} appearance={titleAppearance} />
                        </div>
                        <AnankeIconButton
                            type="button"
                            appearance="subtle"
                            icon={CrossIcon}
                            label="Close"
                            onClick={onClose}
                        />
                    </div>
                    <div className="px-ds-space-300 pb-ds-space-200">{children}</div>
                    <div className="px-ds-space-300 pb-ds-space-300 pt-ds-space-100">{footer}</div>
                </div>
            </div>
        </>,
        document.body,
    );
}

function EventContainerArchiveConfirmationDialog({
    isOpen,
    onClose,
    title,
    children,
    confirmLabel,
    confirmAppearance,
    onConfirm,
    isLoading = false,
}: {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    confirmLabel: string;
    confirmAppearance: "danger" | "warning";
    onConfirm: () => void | Promise<void>;
    isLoading?: boolean;
}) {
    const isMobile = useCheckMobile();
    const mobileSheetMidHeight = useMobileSheetMidHeight();
    const titleAppearance = confirmAppearance;

    const footer = (
        <div className="grid grid-flow-col justify-end items-center gap-ds-space-100">
            <AnankeButton
                type="button"
                appearance="subtle"
                onClick={onClose}
                isDisabled={isLoading === true}
            >
                Cancel
            </AnankeButton>
            <AnankeButton
                type="button"
                appearance={confirmAppearance}
                onClick={() => {
                    void onConfirm();
                }}
                isLoading={isLoading === true}
                isDisabled={isLoading === true}
            >
                {confirmLabel}
            </AnankeButton>
        </div>
    );

    const mobileTitleNode = <ConfirmationTitleWithIcon title={title} appearance={titleAppearance} />;

    if (isMobile === true) {
        return (
            <BottomDrawer
                open={isOpen}
                setOpen={(nextOpen) => {
                    const resolvedOpen = typeof nextOpen === "function" ? nextOpen(isOpen) : nextOpen;
                    if (resolvedOpen !== true) {
                        onClose();
                    }
                }}
                title={mobileTitleNode}
                initialOpenHeight={mobileSheetMidHeight}
                compactLayout={true}
                lockContentScroll={true}
                headerBottomBorder={true}
            >
                <div className="grid gap-ds-space-200 content-start px-4 py-2">
                    {children}
                    {footer}
                </div>
            </BottomDrawer>
        );
    }

    return (
        <DesktopCenteredConfirmationDialog
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            titleAppearance={titleAppearance}
            footer={footer}
        >
            {children}
        </DesktopCenteredConfirmationDialog>
    );
}

export function DeleteEventContainerModal({
    isOpen,
    setIsOpen,
    loading,
    setLoading,
    eventContainer,
}: {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    eventContainer: EventContainerWithEvents;
}) {
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventContainerTemplate = useRequiredEventContainerTemplate(eventContainer.templateId);
    const eventTemplates = useEventTemplates();
    const showErrorFlag = useShowErrorFlag();

    const navigate = useNavigate();

    return (
        <EventContainerArchiveConfirmationDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title="Are you sure you want to delete this event container?"
            confirmLabel="Yes delete"
            confirmAppearance="danger"
            isLoading={loading}
            onConfirm={async () => {
                setIsOpen(false);
                setLoading(true);
                const statusColumnConfig = eventContainerTemplate.configuration.statusColumnConfig;
                if (statusColumnConfig === undefined) {
                    setLoading(false);
                    showErrorFlag("Error", "Status column configuration not found for this Event Container Template");
                    return;
                }
                const result = await archiveEventContainerLikeArchiveDropdown({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl,
                    workspaceId: workspace.id,
                    workspace: workspace,
                    workspaceCapabilities,
                    eventContainer,
                    statusColumnConfig,
                    eventTemplates,
                });
                setLoading(false);
                if (result.success !== true) {
                    showErrorFlag("Error", result.error instanceof Error ? result.error.message : "Could not archive the Event Container");
                    return;
                }
                showSuccessToast("Success", "Event Container and its events were archived successfully");
                navigate({
                    to: configEventContainerListHref(workspace.id),
                });
            }}
        >
            <div
                style={{
                    color: token("color.text"),
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "20px",
                }}
            >
                The Event Container will be archived
            </div>
        </EventContainerArchiveConfirmationDialog>
    );
}

export function ArchiveEventContainerModal({
    isOpen,
    setIsOpen,
    loading,
    setLoading,
    eventContainer,
}: {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    eventContainer: EventContainerWithEvents;
}) {
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const eventContainerTemplate = useRequiredEventContainerTemplate(eventContainer.templateId);
    const eventTemplates = useEventTemplates();
    const showErrorFlag = useShowErrorFlag();

    const navigate = useNavigate();

    const statusColumnConfig = eventContainerTemplate.configuration.statusColumnConfig;

    if (statusColumnConfig === undefined) {
        return null;
    }

    const archiveConfirmationMessage =
        isAmaronWorkspace(workspaceCapabilities) === true
            ? "Are you sure you want to archive this lead? Archived leads can be restored later"
            : "Are you sure you want to archive this Event Container? This action can be reversed later.";

    return (
        <EventContainerArchiveConfirmationDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title="Archive Event Container"
            confirmLabel="Yes, archive"
            confirmAppearance="warning"
            isLoading={loading}
            onConfirm={async () => {
                setIsOpen(false);
                setLoading(true);
                const result = await archiveEventContainerLikeArchiveDropdown({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl,
                    eventContainer,
                    workspaceId: workspace.id,
                    workspace: workspace,
                    workspaceCapabilities,
                    statusColumnConfig,
                    eventTemplates,
                });
                setLoading(false);
                if (result.success !== true) {
                    showErrorFlag("Error", result.error instanceof Error ? result.error.message : "Could not archive the Event Container");
                    return;
                }
                showSuccessToast("Success", "Event Container and its events were archived successfully");
                navigate({
                    to: configEventContainerListHref(workspace.id),
                });
            }}
        >
            <div
                style={{
                    color: token("color.text"),
                    fontFamily: "Inter",
                    fontSize: "14px",
                    lineHeight: "20px",
                }}
            >
                {archiveConfirmationMessage}
            </div>
        </EventContainerArchiveConfirmationDialog>
    );
}
